import React, { useState } from 'react';
import { useParams, Redirect } from 'react-router-dom';
import { Button } from '@material-ui/core';
import {
  Table,
  LoadingSpinner,
  Column,
  Row,
  SortState,
  Box,
  Text,
  createColumn,
  createCell, calculateNewSortState,
} from 'react-limbix-ui';

import { QueryLimbixStudyArgs } from '@/apollo/types';
import { DETERIORATION_ALERTS_QUERY } from '@/apollo/queries';
import {
  UseParamsType,
  DeteriorationAlertsType,
} from '@/types';
import { renderPID } from '@/utils/tableUtils';
import { formatDate } from '@/utils/dateUtils';
import { useModal, usePermissions } from '@/hooks/redux';
import { useQuery } from '@/hooks/apollo';
import { isPermissionDeniedError } from '@/utils/errorUtils';

const DeteriorationAlerts: React.FC = () => {
  const defaultSortState: SortState = { columnId: 'dateCreated', direction: 'desc' };
  const [sortState, setSortState] = useState(defaultSortState);
  const { studyUid } = useParams<UseParamsType>();
  const { showModal } = useModal();
  const [{ studyPermissions }] = usePermissions();
  const { loading, data, error } = useQuery<QueryLimbixStudyArgs>(DETERIORATION_ALERTS_QUERY, {
    variables: {
      studyUid,
    },
  });

  const permissions = studyPermissions[studyUid];

  if (loading) {
    return <LoadingSpinner />;
  }

  if (isPermissionDeniedError(error)) {
    return <Redirect to="/research" />;
  }

  const { limbixStudy } = { ...data };
  const deteriorationAlerts: DeteriorationAlertsType[] = limbixStudy.deteriorationAlerts.map(
    (alert: string): DeteriorationAlertsType => {
      const parsed = JSON.parse(alert);
      return {
        createdAt: new Date(parsed.created_at),
        userUid: parsed.user_uid,
        participantId: parsed.pid,
        baseline: parsed.baseline,
        recentScore: parsed.score,
        scoreDifference: parsed.diff,
      };
    },
  );

  if (deteriorationAlerts.length === 0) {
    return (
      <Text as="h1">
        {'It seems there aren\'t any deterioration alerts'}
        <br />
        If that seems like a mistake please reach out to Research and Eng
      </Text>
    );
  }

  const renderClinicalConcerns = (userUid: string, participantId: string) => (
    <Button
      variant="contained"
      onClick={() => showModal(
        'CLINICAL_CONCERNS',
        {
          userUid,
          participantId,
          studyUid,
          navigate: true,
        },
      )}
    >
      Clinical Concern
    </Button>
  );
  const shouldShowClinicalConcernButton = permissions.canModifyParticipants && !!limbixStudy.clinicalConcernAssessment;

  const columns: Column[] = [
    createColumn('pid', 'PID', 'string', null, true),
    createColumn('dateCreated', 'Date Created', 'date', null, true),
    createColumn('baseline', 'Baseline PHQ Score', 'number', null, true),
    createColumn('deteriorated', 'Deteriorated Score', 'number', null, true),
    createColumn('difference', 'Score Difference', 'date', null, true),
  ];

  if (shouldShowClinicalConcernButton) {
    columns.push(createColumn('clinicalConcerns', 'Clinical Concerns', 'string', null, false));
  }

  const rows: Row[] = deteriorationAlerts.map((alert, index) => {
    const row: Row = {
      id: `${index}`,
      cells: [
        createCell('pid', alert.userUid, renderPID(studyUid, alert.userUid, alert.participantId)),
        createCell('dateCreated', new Date(alert.createdAt), formatDate(alert.createdAt)),
        createCell('baseline', alert.baseline),
        createCell('deteriorated', alert.recentScore),
        createCell('difference', alert.scoreDifference),
      ],
    };
    if (shouldShowClinicalConcernButton) {
      row.cells.push(
        createCell(
          'clinicalConcerns',
          'Clinical Concerns',
          renderClinicalConcerns(alert.userUid, alert.participantId),
        ),
      );
    }
    return row;
  });

  return (
    <Box>
      <Table
        ariaLabel="deterioration_alerts"
        columns={columns}
        rows={rows}
        sortState={sortState}
        onSortByColumn={(colId) => setSortState(calculateNewSortState(colId, sortState))}
      />
    </Box>
  );
};

export default DeteriorationAlerts;
